import {
  ModalHeaderWithBackButton,
  SmsVerificationModal
} from '@local/do-secundo-auth-modals'
import { logError } from '@local/do-secundo-error'
import {
  formatTel,
  getRawPhoneNumberWithCountryCode
} from '@local/do-secundo-form-utils'
import { History } from 'history'
import * as React from 'react'
import { useHistory, useLocation } from 'react-router'
import {
  showLoggedInNotification,
  useAuth
} from '../../../../client/components/AuthProvider/AuthProvider'
import { dataByTypename } from '../../../../client/utils/apollo-helpers'
import { useSendSmsCode, useTrackCodeSubmissionSuccess } from '../index'
import { FROM_LOCATION } from '../utils/enums'

export interface UnifiedSmsVerificationModalProps {
  onClose: () => void
}

interface GetUnifiedSmsModalHeaderParams {
  from: string | undefined
  phone: string | undefined
  history: History<unknown>
  dataIdSuffix: string
}

export const getUnifiedSmsModalHeader = ({
  from,
  phone,
  history,
  dataIdSuffix
}: GetUnifiedSmsModalHeaderParams) => {
  const headerText = (
    <>
      Enter the code sent to
      <br />
      {formatTel(phone!)}
    </>
  )
  if (from) {
    return (
      <ModalHeaderWithBackButton
        onClick={() => history.goBack()}
        headerText={headerText}
        testId={`sms-verification-modal-${dataIdSuffix}-back-button`}
      />
    )
  } else {
    return (
      <h2
        className='mx-0 type-headline-4'
        data-testid={`sms-verification-modal-${dataIdSuffix}-header`}
      >
        {headerText}
      </h2>
    )
  }
}

export const UnifiedSmsVerificationModal = ({
  onClose
}: UnifiedSmsVerificationModalProps) => {
  const {
    authClient,
    setIsAuthenticated,
    getUser,
    searchForCustomermgmtAccount,
    confirmPasswordlessLogin
  } = useAuth()
  const history = useHistory()
  const { state } = useLocation<{
    from?: FROM_LOCATION
    guestPhone?: string
  }>()
  const trackCodeSubmissionSuccess = useTrackCodeSubmissionSuccess()
  const from = state?.from
  const phone = state?.guestPhone
  const dataIdSuffix = state?.from === FROM_LOCATION.login ? 'login' : 'create'
  const header = getUnifiedSmsModalHeader({
    from,
    phone,
    history,
    dataIdSuffix
  })

  React.useEffect(() => {
    if (!phone) {
      history.push('?mode=login')
    }
  }, [history, phone])

  useSendSmsCode(phone, authClient)

  return (
    <SmsVerificationModal
      onClose={onClose}
      header={header}
      handleSubmit={async (code: string) => {
        try {
          const result = await confirmPasswordlessLogin({
            variables: {
              input: {
                code,
                source: 'ONLINE_ORDERING'
              }
            }
          })

          const { ConfirmPasswordlessLoginResponse, PasswordlessLoginError } =
            dataByTypename(result.data.confirmPasswordlessLogin)

          if (PasswordlessLoginError) {
            throw Error(PasswordlessLoginError.message)
          }

          if (from != null) {
            trackCodeSubmissionSuccess(from)
          }

          if (ConfirmPasswordlessLoginResponse.completed) {
            setIsAuthenticated(true)
            onClose()
            showLoggedInNotification(
              ConfirmPasswordlessLoginResponse.profileRequirements.firstName
            )
          } else {
            const profileRequirements =
              ConfirmPasswordlessLoginResponse.profileRequirements
            return history.push('?mode=complete', {
              email: profileRequirements?.email,
              firstName: profileRequirements?.firstName,
              lastName: profileRequirements?.lastName,
              from: FROM_LOCATION.sms
            })
          }
        } catch (error) {
          if (error instanceof TypeError) {
            logError(error)
            throw Error(
              `Toast is experiencing issues. Please continue to place your order without ${
                from === FROM_LOCATION.create
                  ? 'creating an account'
                  : 'logging in'
              }.`
            )
          }
          throw error
        }
      }}
      numFields={6}
      phone={formatTel(phone!)}
      handleResend={() =>
        authClient?.requestPasswordless(getRawPhoneNumberWithCountryCode(phone))
      }
    />
  )
}
